import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../App";
import { Logo } from "../components/Layout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Mail, Lock, ArrowRight, Sparkles } from "lucide-react";
import { MfaChallenge, MfaForcedSetup } from "../components/MfaFlows";

const LoginPage = () => {
  const { user, loginWithPassword, setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login or register
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mfaChallenge, setMfaChallenge] = useState(null);
  const [mfaSetup, setMfaSetup] = useState(null);
  const { registerWithPassword } = useAuth();

  // Redirect if already logged in
  if (user) {
    navigate("/dashboard", { replace: true });
    return null;
  }

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await loginWithPassword(email, password);
      if (result?.mfa_required) {
        setMfaChallenge(result);
        return;
      }
      if (result?.mfa_setup_required) {
        setMfaSetup(result);
        return;
      }
      toast.success("Welcome back!");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSuccess = (userData) => {
    if (userData) setUser(userData);
    toast.success("Welcome back!");
    navigate("/dashboard", { replace: true });
  };

  const cancelMfa = () => {
    setMfaChallenge(null);
    setMfaSetup(null);
    setPassword("");
    setError("");
  };

  const handleEmailRegister = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await registerWithPassword(email, password, name);
      toast.success("Account created! Welcome!");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A1A4A] via-[#1E3A8A] to-[#0F1B3D] relative overflow-hidden" data-testid="login-page">
      {/* Decorative gold accents */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#FFD507]/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#FFD507]/5 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-md mx-auto px-6 py-10 lg:py-14">
        {/* Branded header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center justify-center mb-5 hover:opacity-90 transition-opacity">
            <Logo variant="dark" height={64} />
          </Link>
          <p className="text-[#1E3A8A] text-xs uppercase tracking-[0.2em] font-semibold flex items-center justify-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> Talent Database
          </p>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold text-white mt-2">
            {mode === "login" ? "Welcome Back" : "Join the Academy"}
          </h1>
          <p className="text-slate-300 text-sm mt-2">
            {mode === "login"
              ? "Sign in to access your dashboard, courses, and the Talent Database."
              : "Create an account to enroll in programs and join our alumni network."}
          </p>
        </div>

        <Card className="bg-white shadow-2xl border-0">
          {mfaChallenge || mfaSetup ? (
            <CardContent className="p-0">
              {mfaChallenge && (
                <MfaChallenge challenge={mfaChallenge} onSuccess={handleMfaSuccess} onCancel={cancelMfa} />
              )}
              {mfaSetup && (
                <MfaForcedSetup setup={mfaSetup} onSuccess={handleMfaSuccess} onCancel={cancelMfa} />
              )}
            </CardContent>
          ) : (
          <CardContent className="p-7 space-y-5">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm" data-testid="auth-error">
                {error}
              </div>
            )}

            <form onSubmit={mode === "login" ? handleEmailLogin : handleEmailRegister} className="space-y-4">
              {mode === "register" && (
                <div className="space-y-2">
                  <Label className="text-slate-600">Full Name</Label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="input-light"
                    placeholder="Your full name"
                    required
                    data-testid="input-register-name"
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-slate-600">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-light pl-10"
                    placeholder="you@example.com"
                    required
                    data-testid="input-login-email"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-600">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-light pl-10"
                    placeholder="Min. 6 characters"
                    required
                    minLength={6}
                    data-testid="input-login-password"
                  />
                </div>
              </div>
              {mode === "register" && (
                <div className="space-y-2">
                  <Label className="text-slate-600">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="input-light pl-10"
                      placeholder="Confirm your password"
                      required
                      minLength={6}
                      data-testid="input-confirm-password"
                    />
                  </div>
                </div>
              )}
              <Button
                type="submit"
                disabled={loading}
                className="w-full btn-primary h-11 flex items-center justify-center gap-2"
                data-testid="email-auth-submit"
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    {mode === "login" ? "Sign In" : "Create Account"}
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </Button>
              {mode === "login" && (
                <div className="text-center">
                  <a href="/forgot-password" className="text-sm text-[#1E3A8A] hover:underline font-medium" data-testid="forgot-password-link">
                    Forgot your password?
                  </a>
                </div>
              )}
            </form>

            <div className="text-center text-sm text-slate-500">
              {mode === "login" ? (
                <>
                  Don&apos;t have an account?{" "}
                  <button
                    onClick={() => { setMode("register"); setError(""); }}
                    className="text-[#1E3A8A] hover:underline font-medium"
                    data-testid="switch-to-register"
                  >
                    Create one
                  </button>
                </>
              ) : (
                <>
                  Already have an account?{" "}
                  <button
                    onClick={() => { setMode("login"); setError(""); }}
                    className="text-[#1E3A8A] hover:underline font-medium"
                    data-testid="switch-to-login"
                  >
                    Sign in
                  </button>
                </>
              )}
            </div>

            <div className="pt-2 text-center">
              <Link to="/apply" className="text-sm text-slate-400 hover:text-[#1E3A8A]">
                Want to enroll first? Start your application
              </Link>
            </div>
          </CardContent>
          )}
        </Card>

        <p className="text-center text-xs text-slate-400 mt-6">
          Empowering autistic and neurodivergent talent to launch tech careers.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
